import { ChangeDetectionStrategy, Component, ChangeDetectorRef } from '@angular/core';
import { UrlBuilder } from '../../../core';
import { IBreadcrumb, BreadcrumbService } from '../../shared/breadcrumb-service/breadcrumb.service';
import { FormGroup, FormControl, FormArray, Validators } from '@angular/forms';
import { SampleMonitoringService } from '../service/sample-monitoring-service';
import { FormDropdown, NotificationRuleRequestData, FormDropDownData, SampleMonitorEditRuleRequest, NotificationRuleType, RuleData, NotificationCondition, EventType, MonitorCriteriaJSON, ConditionTypes } from '../interface/notification-data';
import { combineLatest } from 'rxjs';
import { SampleMonitoringStorage } from '../service/sample-monitoring-storage';
import * as moment from 'moment';
import { SampleTrackingDialogService } from '../../service/dialog-service/sample-tracking-dialog-service';
import { SampleTrackerData } from '../../group-info/study-group-info';
import { ActivityLogger } from '../../../activities/activity-logger';
import { ActivatedRoute } from '@angular/router';

@Component({
    selector: 'sm-notification-rule-form',
    template: require('./sm-notification-rule-form.component.html'),
    styles: [require('./sm-notification-rule-form.component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SMNotificationRuleFormComponent {

    public formGroup: FormGroup;
    public showRuleNameLoading = false;
    public protocolList: SampleTrackerData[];
    public protocolData: SampleTrackerData[];
    public eventList: FormDropdown[];
    public specimentTypeList: FormDropdown[];
    public regionList: FormDropdown[];
    public actionList: FormDropdown[];
    public frequencyList: FormDropdown[];
    public formDropdownData: FormDropDownData[] = [];
    public defaultFrequency: FormDropdown[] = [{ key: 1, value: 'Immediate', isChecked: true }];
    public showListLoading = false;
    public showSpecimenAndRegionLoading = false;
    public showSubmitLoading = false;
    public dateErrorMsg = '';
    public ruleNameError = '';
    public isEmailEnabled = false;
    public isEditmode = false;
    public isViewOnlyMode = false;
    public ruleId = 0;
    public ruleData: RuleData;
    public conditionsArray: FormArray;
    public conditionList: FormDropdown[] = [];
    public notificationConditions: NotificationCondition[] = [];
    public includeBatchReturnSamples: boolean;
    public selectedEvent: FormDropdown;
    public isCriteriaLoading = false;
    public filteredConditionsList: FormDropDownData[] = [];
    public isPendingEvent = false;
    private criteriaJSON: MonitorCriteriaJSON;
    private selectedSpecimenTypes: FormDropdown[] = [];
    private selectedProtocol: FormDropdown;
    private isEmailImmediateFlag = true;
    constructor(
        private readonly urlBuilder: UrlBuilder,
        private breadcrumbService: BreadcrumbService,
        private sampleMonitoringService: SampleMonitoringService,
        private smStorage: SampleMonitoringStorage,
        private changeDetectorRef: ChangeDetectorRef,
        private dialogService: SampleTrackingDialogService,
        private activatedRoute: ActivatedRoute,
        activityLogger: ActivityLogger
    ) {
        let portfolioRoute: IBreadcrumb = {
            label: 'Sample Monitoring Settings',
            url: this.urlBuilder.esa().sampletracking().sampleMonitoring().newRuleForm().url()
        };
        this.breadcrumbService.AddRouterToBreadCrumb(portfolioRoute);

        this.formGroup = this.sampleMonitoringService.getSampleMonitoringForm(null, true);
        this.conditionsArray = this.formGroup.get('andCondition') as FormArray;
        this.actionList = this.sampleMonitoringService.actionList;
        this.frequencyList = this.sampleMonitoringService.frequencyList;
        this.loadFromDropdownLists();
        this.onRuleNameChange();
        this.activatedRoute.params.
            subscribe((params) => {
                if (params && params.id) {
                    this.ruleId = params.id;
                }
            });
    
        activityLogger.logActivity('Create New Notification Rule');
    }

    public getFormControl(name: string) {
        if (this.formGroup) {
            return this.formGroup.get(name) as FormControl;
        }
        return null;
    }

    public async onSubmit() {
        this.showSubmitLoading = true;
        let criteria = {
            protocol: { protocolCode: this.selectedProtocol.value, sponsorName: this.selectedProtocol.sponsorName },
            specimenTypes: this.selectedSpecimenTypes.map(t => t.value),
        };
        criteria = this.sampleMonitoringService.addNotificationCritieria(criteria, this.notificationConditions, this.formGroup, this.isPendingEvent);
        let data: NotificationRuleRequestData = {
            ruleName: this.formGroup.get('ruleName').value,
            protocolFk: this.selectedProtocol.key,
            ruleTypeId: this.selectedEvent.key,
            emailFlag: this.isEmailEnabled,
            emailImmediateFlag: this.isEmailImmediateFlag,
            effectiveStartDate: this.formGroup.get('effectiveDate').value,
            effectiveEndDate: this.formGroup.get('expirationDate').value,
            criteria: JSON.stringify(criteria)
        };
        if (this.isEditmode) {
            const updateData = {
                monitorCritera: data.criteria,
                ruleId: this.ruleId,
                monitorRuleName: data.ruleName,
                ...data
            } as SampleMonitorEditRuleRequest;
            this.smStorage.updateRule(updateData).subscribe(res => {
                this.showSubmitLoading = false;
                this.dialogService.showPostActionDialogue('Notification Rule Updated!', true, '0px');
                this.urlBuilder.esa().sampletracking().sampleMonitoring().notificationRules().navigate();
            });
        } else {
            (await this.smStorage.createNewRule(data)).toPromise()
                .then(() => {
                    this.showSubmitLoading = false;
                    this.urlBuilder.esa().sampletracking().sampleMonitoring().notificationRules().navigate();
                })
                .catch(() => {
                    this.dialogService.showPostActionDialogue('Notification Rule not created!', false, '0px');
                    this.showSubmitLoading = false;
                });
        }
    }

    public cancel() {
        this.urlBuilder.esa().sampletracking().sampleMonitoring().notificationRules().navigate();
    }

    public get headerTitle() {
        return !this.ruleId ? 'Create a New Notification Rule' : this.isEditmode ? 'Edit Notification Rule' : 'View Notification Rule';
    }

    public onProtocolSelected($event: FormDropdown) {
        this.showSpecimenAndRegionLoading = true;
        this.selectedProtocol = $event;
        let ruleName = this.formGroup.get('ruleName').value;
        this.checkIfRuleNameExists(ruleName);
        this.formDropdownData = [];
        this.clearConditions();
        this.loadCriteria();
        this.getFormControl('specimenType').markAsUntouched();
        this.selectedSpecimenTypes = [];
        this.showSpecimenAndRegionLoading = false;
    }

    public onEventSelected($event: FormDropdown) {
        this.selectedEvent = $event;
        let ruleName = this.formGroup.get('ruleName').value;
        this.checkIfRuleNameExists(ruleName);
        if ($event) {
            this.loadCriteria();
            this.conditionList = this.sampleMonitoringService.getConditionsList($event.value);
            let ruleType = this.sampleMonitoringService.getRuleEventType($event.value);
            this.isPendingEvent = ruleType === 3 || ruleType === 4 ? true : false;
            this.getFormControl('specimenType').markAsUntouched();
            this.selectedSpecimenTypes = [];
            this.clearConditions();
        } else {
            this.conditionList = [];
            this.formDropdownData = [];
            this.clearConditions();
        }
    }

    public onFrequencySelected($event: FormDropdown) {
        if (!$event) {
            this.isEmailImmediateFlag = false;
            this.defaultFrequency = [];
            return;
        }
        this.defaultFrequency = [$event];
        this.isEmailImmediateFlag = $event.key === 1;
    }

    public onSpecimentTypesSelected($event: FormDropdown[]) {
        this.selectedSpecimenTypes = $event;
    }

    public onExpirationDateAdded($event: any) {
        this.checkDateTime();
    }

    public onRuleNameChange() {
        this.formGroup.get('ruleName').valueChanges.subscribe((ruleName: string) => {
            if (this.isEditmode) {
                this.ruleNameError = '';
                if (this.ruleData && this.ruleData.ruleName !== ruleName) {
                    this.checkIfRuleNameExists(ruleName);
                }
            } else {
                this.checkIfRuleNameExists(ruleName);
            }
        });
    }

    public addNewCondition() {
        this.conditionsArray.push(this.sampleMonitoringService.newCondition());
        let selectedConditions = this.notificationConditions.map(t => t.selectedCondition);
        let newConditionsList = this.sampleMonitoringService.getConditionsList(this.selectedEvent.value);
        if (selectedConditions && selectedConditions.length > 0) {
            newConditionsList.forEach(t => {
                if (selectedConditions.some(value => value.key === t.key)) {
                    t.hide = true;
                }
            }); 
        }
        this.notificationConditions.push({
            conditionList: newConditionsList,
            conditionValueList: [],
            selectedConditionValue: [],
            isAutoSelected: true,
        });
    }

    public disableAddCondition() {
        if (!this.selectedProtocol) { return true; }
        if (this.conditionList.length === 0) { return true; }
        if (this.notificationConditions.length === 0) { return false; }
        if (this.notificationConditions.length === this.conditionList.length) { return true; }
        if (this.notificationConditions[this.notificationConditions.length - 1].selectedCondition) { return false; }
        return true;
    }

    public removeCondition(index: number) {
        this.conditionsArray.removeAt(index);
        let conditions = this.notificationConditions.splice(index, 1);
        let filterData = this.sampleMonitoringService.getFilteredCriteria(conditions, this.formDropdownData);
        if (this.notificationConditions && conditions && conditions.length > 0) {
            if (conditions[0].selectedCondition) {
                this.notificationConditions.forEach((condition, formNumber) => {
                    condition.conditionList.forEach(t => {
                        if (conditions[0].selectedCondition.key === t.key) {
                            t.disable = false;
                            t.hide = false;
                        }
                    });
                    if (formNumber > index) {
                        filterData = this.updateCriteriaList(condition, filterData);
                    }
                });
            }
        }
        this.filteredConditionsList = filterData;
        this.changeDetectorRef.markForCheck();
    }

    public getFormArrayControl(index: number, fieldName: string) {
        let condition = this.conditionsArray.at(index) as FormGroup;
        return condition.get(fieldName) as FormControl;
    }

    public onConditionSelected($event: FormDropdown, formGroupID: number) {
        let existingCondition: FormDropdown;
        this.notificationConditions.forEach((t, index) => {
            if (index === formGroupID) {
                existingCondition = t.selectedCondition;
                t.selectedConditionValue = [];
                t.selectedCondition = $event;
                this.getConditionDropDownList($event, t);
                let condition = this.conditionsArray.at(formGroupID) as FormGroup;
            } else {
                t.conditionList.forEach(condition => {
                    this.setConditionList($event, condition, formGroupID, index, existingCondition);
                });
            }
        });
    }

    public onConditionValuesSelected($event: FormDropdown[], formGroupID: number, isInitialLoad = false) {
        let selectedForm = this.notificationConditions.filter((t, index) => index === formGroupID);
        if (!selectedForm || selectedForm.length === 0) { return; }
        this.notificationConditions[formGroupID].selectedConditionValue = $event;
        let autoSelected = $event && $event.length === selectedForm[0].conditionValueList.length ? true : false;
        this.notificationConditions[formGroupID].isAutoSelected = autoSelected;
        let valueList = isInitialLoad ? this.filteredConditionsList : this.formDropdownData;
        let conditions = this.notificationConditions.filter((t, index) => index <= formGroupID && !t.isAutoSelected);
        let filterData = this.sampleMonitoringService.getFilteredCriteria(conditions, valueList);
        this.filterOtherValues(formGroupID, filterData);
        this.filteredConditionsList = filterData;
        this.changeDetectorRef.markForCheck();
    }

    public filterOtherValues(formGroupID: number, filterData: FormDropDownData[]) {
        this.notificationConditions.forEach((data, index) => {
            if (index > formGroupID) {
                filterData = this.updateCriteriaList(data, filterData);
            }
        });
    }

    public onBatchReturnSamples($event: boolean) {
        this.includeBatchReturnSamples = $event;
    }

    private loadFromDropdownLists() {
        this.showListLoading = true;
        combineLatest([this.smStorage.loadProtocols(), this.smStorage.getRuleTypes()])
            .subscribe(([protocols, ruleTypes]: [SampleTrackerData[],  NotificationRuleType[]]) => {
                this.protocolList = this.protocolData = this.sampleMonitoringService.sortFilterItemsByValue(protocols);
                this.eventList = ruleTypes.map(item => {
                    return {
                        key: item.ruleTypeId,
                        value: item.ruleTypeName,
                        isChecked: false
                    };
                });
                if (!this.isEditmode) {
                    this.selectedEvent = this.eventList.filter(t => t.value === EventType.cancelledSamples)[0];
                    this.conditionList = this.sampleMonitoringService.getConditionsList(EventType.cancelledSamples);
                }
                this.showListLoading = false;
                this.setData();
                this.changeDetectorRef.markForCheck();
            });
    }

    private checkDateTime() {
        const dateBegin = moment(this.formGroup.get('effectiveDate').value);
        const dateEnd = moment(this.formGroup.get('expirationDate').value);

        if (!dateBegin.isValid || !dateEnd.isValid) {
            this.dateErrorMsg = '';
            return;
        }
        if (dateBegin.isAfter(dateEnd)) {
            this.dateErrorMsg = 'Notification End Date should fall after Notification Start Date.';
        } else {
            this.dateErrorMsg = '';
        }
        this.changeDetectorRef.markForCheck();
    }

    private checkIfRuleNameExists(ruleName: string) {
        if (!this.selectedProtocol || (!ruleName || ruleName.length < 3) || !this.selectedEvent) {
            this.ruleNameError = '';
            this.changeDetectorRef.markForCheck();
            return;
        }
        this.smStorage.ruleNameExists({ ruleName: ruleName, protocolFk: this.selectedProtocol.key, ruleTypeId: this.selectedEvent.key })
            .subscribe((result: boolean) => {
                if (result) {
                    this.ruleNameError = 'This Rule name already exists. Please enter an unique rule name.';
                } else {
                    this.ruleNameError = '';
                }
                this.changeDetectorRef.markForCheck();
            });
    }

    private setData() {
        if (this.ruleId) {
            this.showListLoading = true;
            this.smStorage.getRule(this.ruleId)
                .subscribe(res => {
                    let isValidJSON = true;
                    try { JSON.parse(res.monitorCriteria); } catch (e) { isValidJSON = false; }
                    const eventdata = this.eventList.find(el => res.ruleTypeId === el.key);
                    this.isViewOnlyMode = res.deletedFlag === 'T';
                    this.isEditmode = res.deletedFlag === 'F';
                    if (res.monitorCriteria && isValidJSON) {
                        this.criteriaJSON = JSON.parse(res.monitorCriteria);
                    }
                    if (this.criteriaJSON.protocol && this.criteriaJSON.protocol.protocolCode) {
                        this.selectedProtocol = { key: res.protocolFk, value: this.criteriaJSON.protocol.protocolCode, sponsorName: this.criteriaJSON.protocol.sponsorName };
                    }
                    this.isPendingEvent = eventdata.value === EventType.PendingArrivalNonBatch ||
                                          eventdata.value === EventType.PendingArrivalBatch ? true : false;
                    this.ruleData = this.sampleMonitoringService.getRuleData(res, this.criteriaJSON, eventdata, this.selectedSpecimenTypes);
                    this.isEmailImmediateFlag = res.emailImmediateFlag === 'T' ? true : false;
                    this.isEmailEnabled = res.emailFlag === 'T' ? true : false;
                    this.selectedEvent = eventdata;
                    this.conditionList = this.sampleMonitoringService.getConditionsList(eventdata.value);
                    this.formGroup.patchValue(this.ruleData);
                    this.loadCriteria(true, true);
                    this.showListLoading = false;
                    this.changeDetectorRef.markForCheck();
                });
        }
    }

    private setConditionList($event: FormDropdown, condition: FormDropdown, formGroupID: number,
                             index: number, existingCondition: FormDropdown) {
        if (!$event) {
            condition.disable = false;
            condition.hide = false;
        } else if (condition.key === $event.key && index < formGroupID) {
            condition.disable = true;
        } else if (condition.key === $event.key && index > formGroupID) {
            condition.hide = true;
        } else if (existingCondition && existingCondition.key === condition.key) {
            condition.disable = false;
            condition.hide = false;
        }
    }

    private loadCriteria(loadSpecimenType = true, loadNotificationCriteria = false) {
        if (!this.selectedEvent || !this.selectedProtocol) { return; }
        this.isCriteriaLoading = true;
        let ruleEventId = this.sampleMonitoringService.getRuleEventType(this.selectedEvent.value);
        this.smStorage.getNotificationCriteria(ruleEventId, this.selectedProtocol)
            .subscribe(result => {
                this.formDropdownData = this.filteredConditionsList = result;
                if (loadSpecimenType) {
                    this.specimentTypeList = this.sampleMonitoringService.loadDropDownItems(result, 'specimenType');
                }
                if (loadNotificationCriteria) {
                    this.loadNotificationCriteria();
                }
                this.isCriteriaLoading = false;
                this.changeDetectorRef.markForCheck();
            });
    }

    private getConditionDropDownList($event: FormDropdown, notificationCondition: NotificationCondition) {
        if (!$event) {
            notificationCondition.conditionValueList = [];
        } else {
            notificationCondition.conditionValueList = this.sampleMonitoringService.getDropDownList($event.value, this.filteredConditionsList);
        }
    }

    private loadNotificationCriteria() {
        if (this.criteriaJSON.regions) {
            this.addNewNotificationCriteria(ConditionTypes.region, this.criteriaJSON.regions);
        }
        if (this.criteriaJSON.countries) {
            this.addNewNotificationCriteria(ConditionTypes.country, this.criteriaJSON.countries);
        }
        if (this.criteriaJSON.visitDescriptions) {
            this.addNewNotificationCriteria(ConditionTypes.visitDescription, this.criteriaJSON.visitDescriptions);
        }
        if (this.criteriaJSON.protocolVisitCodes) {
            this.addNewNotificationCriteria(ConditionTypes.visitCode, this.criteriaJSON.protocolVisitCodes);
        }
        if (this.criteriaJSON.expectedReceiptConditions) {
            this.addNewNotificationCriteria(ConditionTypes.expectedReceiptCondition, this.criteriaJSON.expectedReceiptConditions);
        }
        if (this.criteriaJSON.investigators) {
            this.addNewNotificationCriteria(ConditionTypes.investigator, this.criteriaJSON.investigators);
        }
        if (this.criteriaJSON.specimenTypes) {
            this.selectedSpecimenTypes = this.specimentTypeList.filter(st => this.criteriaJSON.specimenTypes.some(mc => mc === st.value));
        }
    }

    private addNewNotificationCriteria(conditionName: string,  selectedCriteria: any[]) {
        let condition = this.conditionList.filter(t => t.value === conditionName)[0];
        this.addNewCondition();
        let id = this.notificationConditions.length - 1;
        this.onConditionSelected(condition, id);
        let notificationCondition = this.notificationConditions[id];
        if (selectedCriteria && selectedCriteria.length === 0) {
            this.onConditionValuesSelected(notificationCondition.conditionValueList, id, true);
        } else {
            let selectedValues = notificationCondition.conditionValueList.filter(rl => selectedCriteria.some(mc => mc === rl.value));
            if (condition.loadId) {
                selectedValues = notificationCondition.conditionValueList.filter(rl => selectedCriteria.some(mc => mc === rl.key));
            }
            this.onConditionValuesSelected(selectedValues, id);
        }
    } 

    private updateCriteriaList(condition: NotificationCondition, filterData: FormDropDownData[]) {
        if (condition.selectedCondition) {
            condition.conditionValueList = this.sampleMonitoringService.getDropDownList(condition.selectedCondition.value, filterData);
            let conditions = this.sampleMonitoringService.getFilteredItems(condition.conditionValueList, condition.selectedConditionValue);
            if (condition.isAutoSelected) {
                condition.selectedConditionValue = condition.conditionValueList;
            } else {
                condition.selectedConditionValue = conditions;
            }
        }
        return filterData;
    }

    private clearConditions() {
        this.notificationConditions = [];
        while (this.conditionsArray.length !== 0) {
            this.conditionsArray.removeAt(0);
        }
    }
}
